import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useNavigate } from 'react-router-dom';

const PricingSection = styled.section`
  padding: 40px 20px 80px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  color: #636e72;
  max-width: 600px;
  margin: 0 auto;
`;

const PricingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
`;

const ToggleLabel = styled.span<{ active: boolean }>`
  font-size: 18px;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#2d3436' : '#636e72'};
  cursor: pointer;
  transition: all 0.3s ease;
`;

const ToggleSwitch = styled.div<{ isYearly: boolean }>`
  width: 60px;
  height: 32px;
  background: #ff6b6b;
  border-radius: 16px;
  padding: 4px;
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s ease;
    left: ${props => props.isYearly ? '32px' : '4px'};
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

const PricingCard = styled.div<{ featured?: boolean }>`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: ${props => props.featured
    ? '0 20px 40px rgba(255, 107, 107, 0.15)'
    : '0 10px 20px rgba(0, 0, 0, 0.05)'};
  border: ${props => props.featured ? '2px solid #ff6b6b' : '1px solid #e9ecef'};
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -12px;
  right: 20px;
  background: #ff6b6b;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
`;

const PlanName = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #2d3436;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #25D366;
  }
`;

const PlanPrice = styled.div`
  margin: 20px 0;
`;

const Price = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #2d3436;
  display: flex;
  align-items: flex-start;
  gap: 4px;

  span {
    font-size: 20px;
    font-weight: 600;
    color: #636e72;
  }
`;

const BillingPeriod = styled.div`
  font-size: 16px;
  color: #636e72;
  margin-top: 5px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: #636e72;
  font-size: 16px;

  svg {
    color: #ff6b6b;
  }
`;

const Button = styled.button<{ featured?: boolean }>`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  background: ${props => props.featured ? '#ff6b6b' : 'transparent'};
  color: ${props => props.featured ? 'white' : '#ff6b6b'};
  border: 2px solid #ff6b6b;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: #ff6b6b;
    color: white;
  }
`;

const Pricing: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      id: "plan_single_monthly",
      name: "Single Sub-Account",
      monthlyPrice: 7.99,
      yearlyPrice: 79.9,
      features: [
        "Unlimited WhatsApp messages",
        "Single sub-account",
        "24/7 Support",
        "Message Templates",
        "Basic Analytics"
      ]
    },
    {
      id: "plan_multi_monthly",
      name: "11+ Sub-Accounts",
      monthlyPrice: 5.99,
      yearlyPrice: 59.9,
      featured: true,
      features: [
        "Everything in Single Sub-Account",
        "Minimum 11 sub-accounts",
        "Priority Support",
        "Advanced Analytics",
        "Bulk Messaging Features"
      ]
    }
  ];

  const handleStartFreeTrial = (planId: string) => {
    const token = localStorage.getItem('token');

    if (!token) {
      // User is not logged in, redirect to register page with plan info
      navigate('/login', {
        state: {
          selectedPlan: planId,
          isYearly: isYearly
        }
      });
    } else {
      // User is logged in, redirect to subscription page with plan info
      navigate('/subscription', {
        state: {
          selectedPlan: planId,
          isYearly: isYearly
        }
      });
    }
  };

  return (
    <PricingSection id="pricing">
      <Container>
        <Header>
          <Title>WhatsApp Business API Pricing</Title>
          <Description>
            Simple, transparent pricing for your WhatsApp business needs
          </Description>
        </Header>

        <PricingToggle>
          <ToggleLabel active={!isYearly}>Monthly</ToggleLabel>
          <ToggleSwitch
            isYearly={isYearly}
            onClick={() => setIsYearly(!isYearly)}
          />
          <ToggleLabel active={isYearly}>Yearly</ToggleLabel>
        </PricingToggle>

        <PricingGrid>
          {plans.map((plan) => (
            <PricingCard key={plan.id} featured={plan.featured}>
              {plan.featured && <Badge>Most Popular</Badge>}
              <PlanName>
                <FontAwesomeIcon icon={faWhatsapp} />
                {plan.name} ({isYearly ? 'Yearly' : 'Monthly'})
              </PlanName>
              <PlanPrice>
                <Price>
                  ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                  <span>/{isYearly ? 'year' : 'month'}</span>
                </Price>
                <BillingPeriod>Per Sub-Account</BillingPeriod>
              </PlanPrice>
              <FeatureList>
                {plan.features.map((feature, i) => (
                  <Feature key={i}>
                    <FontAwesomeIcon icon={faCheck} />
                    {feature}
                  </Feature>
                ))}
              </FeatureList>
              <Button
                featured={plan.featured}
                onClick={() => handleStartFreeTrial(plan.id)}
              >
                Start 14 Days Free Trial!
              </Button>
            </PricingCard>
          ))}
        </PricingGrid>
      </Container>
    </PricingSection>
  );
};

export default Pricing; 